import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {
  getDataverseEnvironmentUrl,
  getDataverseApiUrl
} from '../config/dataverse';
import { AccessDeniedError } from '../errors/AccessDeniedError';
import {
  isDataverseEntityMissingError,
  throwIfEntitySetMissing
} from '../errors/DataverseEntityMissingError';
import { formatDataverseRecordKey } from '../lib/dataverseRecordId';
import { mapDataverseLock } from '../config/lockProviders';
import { createKeyRequestService } from './keyRequestService';
import { createLockSyncService } from './lockSyncService';
import { createPhoneService } from './phoneService';
import { createPhoneSessionService } from './phoneSessionService';
import { createAccessGroupService } from './accessGroupService';
import { createOpencareLogService } from './opencareLogService';
import { createMobilLogService } from './mobilLogService';
import { createOperationsSettingsService } from './operationsSettingsService';
import { getDisplayCustomerName } from '../lib/customerDisplay';
import { getUnlocScopes } from '../authConfig';

function getDataverseConfig() {
  const environmentUrl = getDataverseEnvironmentUrl();
  return {
    environmentUrl,
    apiUrl: getDataverseApiUrl(environmentUrl)
  };
}

async function readAccessDeniedMessage(response) {
  const customerName = getDisplayCustomerName() || 'denne kommunen';
  let detail = '';
  try {
    const text = await response.text();
    if (text) {
      const json = JSON.parse(text);
      detail = json?.error?.message || json?.Message || '';
    }
  } catch {
    detail = '';
  }

  if (response.status === 401) {
    return `Økten er utløpt eller du er ikke innlogget mot ${customerName}.`;
  }

  if (detail) {
    return `Du har ikke tilgang til denne handlingen i ${customerName}. ${detail}`;
  }

  return `Du har ikke tilgang til denne handlingen i ${customerName}. Be en administrator tildele rollen OpenCare Users.`;
}

// MSAL instance for token acquisition
let msalInstance = null;

export const initializeDataverseService = (msal) => {
  msalInstance = msal;
};

async function acquireAccessToken(scopes, { allowPopup = false } = {}) {
  if (!msalInstance) {
    throw new Error('MSAL instance not initialized');
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No accounts found');
  }

  const tokenRequest = {
    scopes,
    account: msalInstance.getActiveAccount() || accounts[0]
  };

  try {
    const response = await msalInstance.acquireTokenSilent(tokenRequest);
    return response.accessToken;
  } catch (silentError) {
    if (!(silentError instanceof InteractionRequiredAuthError)) {
      throw silentError;
    }

    // Unloc is a separate resource — prefer popup so LogsPage can continue after consent.
    if (allowPopup && typeof msalInstance.acquireTokenPopup === 'function') {
      try {
        const popupResponse = await msalInstance.acquireTokenPopup(tokenRequest);
        return popupResponse.accessToken;
      } catch (popupError) {
        console.warn('Unloc popup consent failed, falling back to redirect:', popupError);
      }
    }

    await msalInstance.acquireTokenRedirect(tokenRequest);
    return null;
  }
}

const getAccessToken = async () => {
  try {
    const { environmentUrl } = getDataverseConfig();
    return await acquireAccessToken([`${environmentUrl}/.default`]);
  } catch (error) {
    console.error('Error acquiring Dataverse token:', error);
    throw error;
  }
};

export const getUnlocAccessToken = async () => {
  try {
    return await acquireAccessToken(getUnlocScopes().scopes, { allowPopup: true });
  } catch (error) {
    console.error('Error acquiring Unloc token:', error);
    throw error;
  }
};

// Generic API call to Dataverse
const dataverseApiCall = async (endpoint, options = {}) => {
  try {
    const token = await getAccessToken();
    if (!token) {
      return null;
    }

    const { headers: optionHeaders, ...restOptions } = options;
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'OData-MaxVersion': '4.0',
        'OData-Version': '4.0'
      }
    };

    const finalOptions = {
      ...defaultOptions,
      ...restOptions,
      headers: {
        ...defaultOptions.headers,
        ...optionHeaders
      }
    };
    const { apiUrl } = getDataverseConfig();
    const url =
      endpoint.startsWith('https://') || endpoint.startsWith('http://')
        ? endpoint
        : `${apiUrl}${endpoint}`;

    const response = await fetch(url, finalOptions);

    if (response.status === 401 || response.status === 403) {
      throw new AccessDeniedError(
        await readAccessDeniedMessage(response),
        response.status
      );
    }

    if (!response.ok) {
      throwIfEntitySetMissing(endpoint, finalOptions.method, response.status);
      let detail = '';
      try {
        const text = await response.text();
        if (text) {
          const json = JSON.parse(text);
          detail = json?.error?.message || text;
        }
      } catch {
        detail = '';
      }
      throw new Error(
        detail
          ? `Dataverse API error: ${response.status} ${detail}`
          : `Dataverse API error: ${response.status} ${response.statusText}`
      );
    }

    // Handle empty responses (204 No Content) for POST/PATCH/DELETE operations
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return null;
    }

    // Get response text first to check if it's empty
    const responseText = await response.text();
    
    // If response is empty, return null
    if (!responseText || responseText.trim() === '') {
      return null;
    }

    // Try to parse as JSON if there's content
    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      console.warn('Failed to parse response as JSON:', parseError);
      return null;
    }
  } catch (error) {
    if (error instanceof AccessDeniedError) {
      throw error;
    }
    console.error('Dataverse API call failed:', error);
    throw error;
  }
};

const RESIDENT_SELECT =
  'oc_innbyggerid,oc_name,oc_adresse,oc_nin,oc_epjid,oc_carelevel,statuscode,createdon';

function mapResident(resident) {
  const recordId = resident.oc_innbyggerid;

  return {
    id: recordId,
    recordId,
    residentId: recordId,
    name: resident.oc_name,
    room: resident.oc_adresse || 'Ikke oppgitt',
    nin: resident.oc_nin || null,
    epjId: resident.oc_epjid || null,
    careLevel: resident.oc_carelevel || null,
    admissionDate: resident.createdon
      ? resident.createdon.split('T')[0]
      : new Date().toISOString().split('T')[0],
    status: resident.statuscode === 1 ? 'Aktiv' : 'Inaktiv',
    assignedLocks: resident.oc_Lock_Innbygger?.map((a) => a.oc_lockid) || []
  };
}

// Resident operations
export const residentService = {
      // Get all residents with their assigned locks
      getAll: async () => {
        const withExpand =
          `/oc_innbyggers?$select=${RESIDENT_SELECT}&$expand=oc_Lock_Innbygger($select=oc_lockid)&$orderby=oc_name`;
        const withoutExpand = `/oc_innbyggers?$select=${RESIDENT_SELECT}&$orderby=oc_name`;

        try {
          const response = await dataverseApiCall(withExpand);
          return (response.value || []).map(mapResident);
        } catch (error) {
          console.warn('Resident expand query failed, retrying without relationships:', error);
          const response = await dataverseApiCall(withoutExpand);
          return (response.value || []).map(mapResident);
        }
      },

      // Create new resident
      create: async (residentData) => {
        const payload = {
          oc_name: residentData.name,
          oc_adresse: residentData.room,
          statuscode: 1 // Active
        };

        // Only include NIN if it's not empty (keep as string to preserve leading zeros)
        if (residentData.nin && String(residentData.nin).trim() !== '') {
          payload.oc_nin = String(residentData.nin).trim();
        }

        // Include EPJ ID if provided
        if (residentData.epjId && String(residentData.epjId).trim() !== '') {
          payload.oc_epjid = parseInt(residentData.epjId) || residentData.epjId;
        }

        const response = await dataverseApiCall('/oc_innbyggers', {
          method: 'POST',
          body: JSON.stringify(payload)
        });

        // Generate a temporary ID if response is null (POST operations often return null)
        const generatedId = response?.oc_innbyggerid || `temp_${Date.now()}`;
        
        return {
          id: generatedId,
          residentId: generatedId,
          ...residentData,
          assignedLocks: []
        };
      },

      // Update resident
      update: async (residentId, residentData) => {
        const payload = {
          oc_name: residentData.name,
          oc_adresse: residentData.room
        };

        // Only include NIN if it's not empty (keep as string to preserve leading zeros)
        if (residentData.nin && String(residentData.nin).trim() !== '') {
          payload.oc_nin = String(residentData.nin).trim();
        }

        // Include EPJ ID if provided
        if (residentData.epjId && String(residentData.epjId).trim() !== '') {
          payload.oc_epjid = parseInt(residentData.epjId) || residentData.epjId;
        }

        await dataverseApiCall(`/oc_innbyggers(${formatDataverseRecordKey(residentId)})`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });

        return { ...residentData };
      },

  // Delete resident (or deactivate if delete fails)
  delete: async (residentId) => {
    const recordKey = formatDataverseRecordKey(residentId);

    try {
      // First, remove all lock assignments for this resident
      try {
        const assignments = await assignmentService.getAll();
        const residentAssignments = assignments.filter((a) => a.residentId === residentId);

        for (const assignment of residentAssignments) {
          await assignmentService.remove(assignment.id);
        }
      } catch (assignmentError) {
        console.warn('Error removing assignments before delete:', assignmentError);
      }

      await dataverseApiCall(`/oc_innbyggers(${recordKey})`, {
        method: 'DELETE'
      });
    } catch (deleteError) {
      console.warn('Delete failed, deactivating resident instead:', deleteError);

      await dataverseApiCall(`/oc_innbyggers(${recordKey})`, {
        method: 'PATCH',
        body: JSON.stringify({
          statuscode: 0
        })
      });
    }
  }
};

// Lock operations
const LOCK_SELECT_EXTENDED_WITH_COORDS_AND_NOTE =
  'oc_lockid,oc_name,oc_beskrivelse,oc_vendor,oc_vendortype,oc_provider,oc_externalid,oc_newcolumn,oc_imageurl,statuscode,createdon,oc_batterylevel,oc_canbelocked,oc_latitude,oc_longitude,oc_city,oc_duplicatelock,oc_duplicatesourcelockid,oc_duplicatesourcelockname,oc_projectid,oc_projectname,oc_projectorganizationid,oc_projectorganizationname,oc_locationsource,oc_unloccreatedon,oc_unlocupdatedon,oc_notat';
const LOCK_SELECT_EXTENDED_WITH_COORDS =
  'oc_lockid,oc_name,oc_beskrivelse,oc_vendor,oc_vendortype,oc_provider,oc_externalid,oc_newcolumn,oc_imageurl,statuscode,createdon,oc_batterylevel,oc_canbelocked,oc_latitude,oc_longitude,oc_city,oc_duplicatelock,oc_duplicatesourcelockid,oc_duplicatesourcelockname,oc_projectid,oc_projectname,oc_projectorganizationid,oc_projectorganizationname,oc_locationsource,oc_unloccreatedon,oc_unlocupdatedon';
const LOCK_SELECT_EXTENDED =
  'oc_lockid,oc_name,oc_beskrivelse,oc_vendor,oc_vendortype,oc_provider,oc_externalid,oc_newcolumn,oc_imageurl,statuscode,createdon,oc_batterylevel,oc_canbelocked,oc_city,oc_duplicatelock,oc_duplicatesourcelockid,oc_duplicatesourcelockname,oc_projectid,oc_projectname,oc_projectorganizationid,oc_projectorganizationname,oc_locationsource,oc_unloccreatedon,oc_unlocupdatedon';
const LOCK_SELECT_WITH_COORDS =
  'oc_lockid,oc_name,oc_beskrivelse,oc_vendor,oc_provider,oc_externalid,oc_newcolumn,oc_imageurl,statuscode,createdon,oc_batterylevel,oc_canbelocked,oc_latitude,oc_longitude';
const LOCK_SELECT_FULL =
  'oc_lockid,oc_name,oc_beskrivelse,oc_vendor,oc_provider,oc_externalid,oc_newcolumn,oc_imageurl,statuscode,createdon,oc_batterylevel,oc_canbelocked';
const LOCK_SELECT_BASIC =
  'oc_lockid,oc_name,oc_beskrivelse,oc_vendor,oc_newcolumn,oc_imageurl,statuscode,createdon,oc_batterylevel,oc_canbelocked';
const ACTIVE_LOCK_FILTER = 'statecode eq 0';
const LOCK_DETAIL_SELECT =
  'oc_lockid,oc_imageurl,oc_city,oc_vendortype,oc_locationsource,oc_duplicatesourcelockid,oc_duplicatesourcelockname,oc_projectid,oc_projectname,oc_projectorganizationid,oc_projectorganizationname,oc_unloccreatedon,oc_unlocupdatedon';

function needsUnlocDetailHydration(lock) {
  return (
    lock.provider === 'unloc' &&
    !lock.city &&
    !lock.vendorType &&
    !lock.locationSource &&
    !lock.unlocCreatedOn &&
    !lock.unlocUpdatedOn &&
    !lock.duplicateSourceLock
  );
}

async function hydrateUnlocLockDetails(locks) {
  const targets = locks
    .map((lock, index) => ({ lock, index }))
    .filter(({ lock }) => needsUnlocDetailHydration(lock));

  if (targets.length === 0) {
    return locks;
  }

  const enriched = await Promise.all(
    targets.map(async ({ lock, index }) => {
      try {
        const record = await dataverseApiCall(
          `/oc_locks(${formatDataverseRecordKey(lock.lockId)})?$select=${LOCK_DETAIL_SELECT}`
        );
        const mapped = mapDataverseLock(record || {});

        return {
          index,
          lock: {
            ...lock,
            city: mapped.city || lock.city,
            vendorType: mapped.vendorType || lock.vendorType,
            locationSource: mapped.locationSource || lock.locationSource,
            unlocCreatedOn: mapped.unlocCreatedOn || lock.unlocCreatedOn,
            unlocUpdatedOn: mapped.unlocUpdatedOn || lock.unlocUpdatedOn,
            duplicateSourceLock: mapped.duplicateSourceLock || lock.duplicateSourceLock
          }
        };
      } catch (error) {
        console.warn(`Could not hydrate lock details for ${lock.lockId}:`, error);
        return { index, lock };
      }
    })
  );

  const next = [...locks];
  enriched.forEach(({ index, lock }) => {
    next[index] = lock;
  });
  return next;
}

function mapLockDetails(record) {
  const mapped = mapDataverseLock(record || {});
  return {
    imageUrl: mapped.imageUrl || null,
    city: mapped.city || null,
    vendorType: mapped.vendorType || null,
    locationSource: mapped.locationSource || null,
    unlocCreatedOn: mapped.unlocCreatedOn || null,
    unlocUpdatedOn: mapped.unlocUpdatedOn || null,
    duplicateSourceLock: mapped.duplicateSourceLock || null
  };
}

export const lockService = {
  getAll: async () => {
    const queries = [
      `/oc_locks?$select=${LOCK_SELECT_EXTENDED_WITH_COORDS_AND_NOTE}&$filter=${ACTIVE_LOCK_FILTER}&$orderby=oc_name`,
      `/oc_locks?$select=${LOCK_SELECT_EXTENDED_WITH_COORDS}&$filter=${ACTIVE_LOCK_FILTER}&$orderby=oc_name`,
      `/oc_locks?$select=${LOCK_SELECT_EXTENDED}&$filter=${ACTIVE_LOCK_FILTER}&$orderby=oc_name`,
      `/oc_locks?$select=${LOCK_SELECT_WITH_COORDS}&$filter=${ACTIVE_LOCK_FILTER}&$orderby=oc_name`,
      `/oc_locks?$select=${LOCK_SELECT_FULL}&$filter=${ACTIVE_LOCK_FILTER}&$orderby=oc_name`,
      `/oc_locks?$select=${LOCK_SELECT_FULL}&$filter=${ACTIVE_LOCK_FILTER}&$orderby=oc_name`,
      `/oc_locks?$select=${LOCK_SELECT_BASIC}&$filter=${ACTIVE_LOCK_FILTER}&$orderby=oc_name`
    ];

    let lastError;
    for (const endpoint of queries) {
      try {
        const response = await dataverseApiCall(endpoint);
        const locks = (response.value || []).map(mapDataverseLock);
        return await hydrateUnlocLockDetails(locks);
      } catch (error) {
        if (isDataverseEntityMissingError(error)) {
          throw error;
        }
        lastError = error;
        console.warn('Lock query failed, trying simpler query:', error);
      }
    }

    throw lastError;
  },

  getDetails: async (lockId) => {
    const record = await dataverseApiCall(
      `/oc_locks(${formatDataverseRecordKey(lockId)})?$select=${LOCK_DETAIL_SELECT}`
    );
    return mapLockDetails(record);
  },

  updateNotat: async (lockId, text) => {
    const note =
      text == null || String(text).trim() === '' ? null : String(text);
    await dataverseApiCall(`/oc_locks(${formatDataverseRecordKey(lockId)})`, {
      method: 'PATCH',
      body: JSON.stringify({ oc_notat: note })
    });
    return note;
  }
};

// Assignment operations
export const assignmentService = {
  // Get all assignments by getting locks with their residents
  getAll: async () => {
    try {
      const response = await dataverseApiCall(
        `/oc_locks?$select=oc_lockid&$filter=${ACTIVE_LOCK_FILTER}&$expand=oc_Lock_Innbygger($select=oc_innbyggerid,oc_name)`
      );

      const assignments = [];
      response.value?.forEach((lock) => {
        if (lock.oc_Lock_Innbygger) {
          lock.oc_Lock_Innbygger.forEach((assignment) => {
            assignments.push({
              id: `${lock.oc_lockid}_${assignment.oc_innbyggerid}`,
              residentId: assignment.oc_innbyggerid,
              lockId: lock.oc_lockid,
              assignedDate: new Date().toISOString().split('T')[0],
              assignedBy: 'System',
              status: 'Aktiv'
            });
          });
        }
      });

      return assignments;
    } catch (error) {
      if (isDataverseEntityMissingError(error)) {
        return [];
      }
      console.warn('Could not load assignments:', error);
      return [];
    }
  },

  // Create new assignment using association endpoint
  create: async (assignmentData) => {
    const { environmentUrl } = getDataverseConfig();
    const residentKey = formatDataverseRecordKey(assignmentData.residentId);
    const lockKey = formatDataverseRecordKey(assignmentData.lockId);

    await dataverseApiCall(`/oc_innbyggers(${residentKey})/oc_Lock_Innbygger/$ref`, {
      method: 'POST',
      body: JSON.stringify({
        '@odata.id': `${environmentUrl}/api/data/v9.2/oc_locks(${lockKey})`
      })
    });

    return {
      id: `${assignmentData.residentId}_${assignmentData.lockId}`, // Create unique ID
      ...assignmentData,
      assignedDate: new Date().toISOString().split('T')[0],
      assignedBy: 'System',
      status: 'Aktiv'
    };
  },

  // Remove assignment using association endpoint
  remove: async (assignmentId) => {
    const [lockId, residentId] = assignmentId.split('_');

    await dataverseApiCall(
      `/oc_innbyggers(${formatDataverseRecordKey(residentId)})/oc_Lock_Innbygger(${formatDataverseRecordKey(lockId)})/$ref`,
      { method: 'DELETE' }
    );
  }
};

export const keyRequestService = createKeyRequestService(dataverseApiCall);
export const phoneService = createPhoneService(dataverseApiCall);
export const opencareLogService = createOpencareLogService(dataverseApiCall);
export const mobilLogService = createMobilLogService(dataverseApiCall);
export const operationsSettingsService = createOperationsSettingsService(dataverseApiCall);
export const accessGroupService = createAccessGroupService(
  dataverseApiCall,
  () => getDataverseEnvironmentUrl()
);
export const lockSyncService = createLockSyncService(dataverseApiCall, accessGroupService);
export const phoneSessionService = createPhoneSessionService({
  dataverseApiCall,
  phoneService,
  accessGroupService,
  lockService,
  mobilLogService
});

// Combined service with relationships
export const dataverseService = {
  // Expose individual services
  residentService,
  lockService,
  assignmentService,
  keyRequestService,
  lockSyncService,
  phoneService,
  phoneSessionService,
  opencareLogService,
  mobilLogService,
  accessGroupService,
  operationsSettingsService,
  getUnlocAccessToken,

  // Get residents with their assigned locks
  getResidentsWithLocks: async () => {
    const [residentsResult, assignmentsResult] = await Promise.allSettled([
      residentService.getAll(),
      assignmentService.getAll()
    ]);

    if (residentsResult.status === 'rejected') {
      throw residentsResult.reason;
    }

    const residents = residentsResult.value;
    const assignments =
      assignmentsResult.status === 'fulfilled' ? assignmentsResult.value : [];

    if (assignmentsResult.status === 'rejected') {
      console.warn('Could not load lock assignments:', assignmentsResult.reason);
    }

    // Group assignments by resident
    const assignmentsByResident = assignments.reduce((acc, assignment) => {
      if (!acc[assignment.residentId]) {
        acc[assignment.residentId] = [];
      }
      acc[assignment.residentId].push(assignment.lockId);
      return acc;
    }, {});

    // Add assigned locks to residents
    return residents.map(resident => ({
      ...resident,
      assignedLocks: assignmentsByResident[resident.residentId] || []
    }));
  },

  // Get locks with their assigned residents
  getLocksWithResidents: async () => {
    const [locksResult, assignmentsResult] = await Promise.allSettled([
      lockService.getAll(),
      assignmentService.getAll()
    ]);

    if (locksResult.status === 'rejected') {
      throw locksResult.reason;
    }

    const locks = locksResult.value;
    const assignments =
      assignmentsResult.status === 'fulfilled' ? assignmentsResult.value : [];

    if (assignmentsResult.status === 'rejected') {
      console.warn('Could not load lock assignments:', assignmentsResult.reason);
    }

    // Group assignments by lock
    const assignmentsByLock = assignments.reduce((acc, assignment) => {
      if (!acc[assignment.lockId]) {
        acc[assignment.lockId] = [];
      }
      acc[assignment.lockId].push(assignment.residentId);
      return acc;
    }, {});

    // Add assigned residents to locks
    return locks.map(lock => ({
      ...lock,
      assignedResidents: assignmentsByLock[lock.lockId] || []
    }));
  },

  // Get complete data for assignments page
  getAssignmentsData: async () => {
    const [residentsResult, locksResult, assignmentsResult] = await Promise.allSettled([
      residentService.getAll(),
      lockService.getAll(),
      assignmentService.getAll()
    ]);

    const errors = [residentsResult, locksResult, assignmentsResult]
      .filter((result) => result.status === 'rejected')
      .map((result) => result.reason);

    const entityMissing = errors.find(isDataverseEntityMissingError);
    if (entityMissing) {
      throw entityMissing;
    }

    if (residentsResult.status === 'rejected') {
      throw residentsResult.reason;
    }

    return {
      residents: residentsResult.value,
      locks: locksResult.status === 'fulfilled' ? locksResult.value : [],
      assignments: assignmentsResult.status === 'fulfilled' ? assignmentsResult.value : []
    };
  }
};

export default dataverseService;
